import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCartStore } from '../stores/cartStore';
import { useEffect, useState } from 'react';
import { CurrencySelector } from '../components/common/CurrencySelector';
import { NotificationDropdown } from '../components/notifications/NotificationDropdown';
import {
  Package,
  Users,
  MessageSquare,
  LogIn,
  UserPlus,
  LayoutDashboard,
  LogOut,
  X,
  ShoppingCart,
} from 'lucide-react';

export default function MainLayout() {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { itemCount, fetchCart } = useCartStore();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated, fetchCart]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-gold/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" onClick={closeMenu}>
              <span className="font-western text-2xl text-gold">OutlawOasis</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/products" className="text-cream hover:text-gold transition-colors">
                Products
              </Link>
              <Link to="/sellers" className="text-cream hover:text-gold transition-colors">
                Sellers
              </Link>
              <a href="https://forum.outlawoasis.com" className="text-cream hover:text-gold transition-colors">
                Forum
              </a>
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
              <CurrencySelector />

              {/* Cart */}
              <Link to="/cart" className="relative p-2 text-cream hover:text-gold transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-hot text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>

              {/* Auth */}
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <NotificationDropdown />
                  <Link to="/dashboard" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-leather border border-gold/30 flex items-center justify-center overflow-hidden">
                      {user?.avatarUrl ? (
                        <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-gold text-sm font-semibold">
                          {user?.username?.[0]?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="hidden sm:block text-cream">{user?.displayName || user?.username}</span>
                  </Link>
                  <button onClick={handleLogout} className="text-text-muted hover:text-cream transition-colors">
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <Link to="/login" className="text-cream hover:text-gold transition-colors">
                    Login
                  </Link>
                  <Link to="/register" className="btn-primary text-sm py-2 px-4">
                    Sign Up
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile: Quick Icons + Hamburger */}
            <div className="flex md:hidden items-center gap-1">
              {/* Cart (always visible) */}
              <Link to="/cart" className="relative p-2 text-cream hover:text-gold transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-hot text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {itemCount}
                  </span>
                )}
              </Link>

              {/* Notifications (authenticated only) */}
              {isAuthenticated && <NotificationDropdown />}

              {/* Hamburger Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-cream hover:text-gold transition-colors ml-1"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                <div className="w-6 flex flex-col gap-[5px]">
                  <span
                    className={`block h-[2px] bg-current rounded-full transition-all duration-300 ease-in-out origin-center ${
                      mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''
                    }`}
                  />
                  <span
                    className={`block h-[2px] bg-current rounded-full transition-all duration-300 ease-in-out ${
                      mobileMenuOpen ? 'opacity-0 scale-x-0' : ''
                    }`}
                  />
                  <span
                    className={`block h-[2px] bg-current rounded-full transition-all duration-300 ease-in-out origin-center ${
                      mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''
                    }`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-charcoal/80 backdrop-blur-sm"
          onClick={closeMenu}
        />

        {/* Slide-in Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-leather flex flex-col shadow-2xl transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          } ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}
        >
          {/* Gold accent edge */}
          <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-gold/60 to-transparent" />

          {/* Panel Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gold/10">
            <span className="font-western text-xl text-gold tracking-wide">OutlawOasis</span>
            <button
              onClick={closeMenu}
              className="p-1.5 text-text-muted hover:text-cream rounded-lg hover:bg-leather-mid transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info (authenticated) */}
          {isAuthenticated && (
            <div className="px-5 py-4 border-b border-gold/10 bg-leather-mid/40">
              <Link to="/dashboard" onClick={closeMenu} className="flex items-center gap-3 group">
                <div className="w-11 h-11 rounded-full bg-leather border-2 border-gold/30 group-hover:border-gold/60 flex items-center justify-center overflow-hidden flex-shrink-0 transition-colors duration-200">
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-gold font-semibold text-lg">
                      {user?.username?.[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-cream font-medium truncate group-hover:text-gold transition-colors duration-200">
                    {user?.displayName || user?.username}
                  </p>
                  <p className="text-text-muted text-xs">@{user?.username}</p>
                </div>
              </Link>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
            <p className="px-3 pb-2 text-[11px] font-semibold text-text-muted uppercase tracking-widest">
              Navigate
            </p>

            <Link
              to="/products"
              onClick={closeMenu}
              className="mobile-nav-item flex items-center gap-3 px-3 py-3 rounded-lg text-text-secondary hover:bg-leather-mid hover:text-cream transition-colors"
              style={{ animationDelay: '0.06s' }}
            >
              <Package className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Products</span>
            </Link>

            <Link
              to="/sellers"
              onClick={closeMenu}
              className="mobile-nav-item flex items-center gap-3 px-3 py-3 rounded-lg text-text-secondary hover:bg-leather-mid hover:text-cream transition-colors"
              style={{ animationDelay: '0.11s' }}
            >
              <Users className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Sellers</span>
            </Link>

            <a
              href="https://forum.outlawoasis.com"
              className="mobile-nav-item flex items-center gap-3 px-3 py-3 rounded-lg text-text-secondary hover:bg-leather-mid hover:text-cream transition-colors"
              style={{ animationDelay: '0.16s' }}
            >
              <MessageSquare className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Forum</span>
            </a>

            <Link
              to="/cart"
              onClick={closeMenu}
              className="mobile-nav-item flex items-center gap-3 px-3 py-3 rounded-lg text-text-secondary hover:bg-leather-mid hover:text-cream transition-colors"
              style={{ animationDelay: '0.21s' }}
            >
              <ShoppingCart className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Cart</span>
              {itemCount > 0 && (
                <span className="ml-auto bg-hot text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {itemCount}
                </span>
              )}
            </Link>

            {isAuthenticated && (
              <>
                <div className="pt-4">
                  <p className="px-3 pb-2 text-[11px] font-semibold text-text-muted uppercase tracking-widest">
                    Account
                  </p>
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    className="mobile-nav-item flex items-center gap-3 px-3 py-3 rounded-lg text-text-secondary hover:bg-leather-mid hover:text-cream transition-colors"
                    style={{ animationDelay: '0.26s' }}
                  >
                    <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Dashboard</span>
                  </Link>
                </div>
              </>
            )}

            {/* Currency selector */}
            <div className="pt-4 px-3">
              <p className="pb-2 text-[11px] font-semibold text-text-muted uppercase tracking-widest">
                Currency
              </p>
              <CurrencySelector />
            </div>
          </nav>

          {/* Bottom Auth Actions */}
          <div className="px-4 py-4 border-t border-gold/10 bg-leather-mid/20">
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-hot hover:bg-leather-mid transition-colors font-medium"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gold/30 text-cream hover:bg-leather-mid transition-colors text-sm font-medium"
                >
                  <LogIn className="w-4 h-4" />
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={closeMenu}
                  className="btn-primary w-full text-sm"
                >
                  <UserPlus className="w-4 h-4" />
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-leather border-t border-gold/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-western text-xl text-gold mb-4">OutlawOasis</h3>
              <p className="text-text-secondary text-sm">
                The premier marketplace for digital goods. Quality products from verified sellers.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-cream mb-4">Marketplace</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><Link to="/products" className="hover:text-gold transition-colors">Browse Products</Link></li>
                <li><Link to="/sellers" className="hover:text-gold transition-colors">Our Sellers</Link></li>
                <li><Link to="/categories" className="hover:text-gold transition-colors">Categories</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-cream mb-4">Community</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><a href="https://forum.outlawoasis.com" className="hover:text-gold transition-colors">Forum</a></li>
                <li><Link to="/sellers/apply" className="hover:text-gold transition-colors">Become a Seller</Link></li>
                <li><Link to="/support" className="hover:text-gold transition-colors">Support</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-cream mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><Link to="/terms" className="hover:text-gold transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-gold transition-colors">Privacy Policy</Link></li>
                <li><Link to="/refunds" className="hover:text-gold transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gold/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-text-muted text-sm">
              &copy; {new Date().getFullYear()} Outlaw Oasis. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <CurrencySelector variant="footer" />
              <div className="flex items-center gap-4">
                <span className="text-text-muted text-sm">We accept:</span>
                <div className="flex items-center gap-2">
                  <span className="text-text-secondary text-sm">Cards</span>
                  <span className="text-text-muted">•</span>
                  <span className="text-text-secondary text-sm">Crypto</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
